/**
 * Sidebar Hooks
 * 
 * Compatibility layer for migrating from SidebarProvider to Zustand.
 * These hooks provide the same API as the old SidebarProvider context,
 * but use Zustand under the hood for better performance.
 */

import { useEffect } from 'react';
import { useBoundStore, useStoreShallow } from '@/stores';
import type { SidebarState } from '@/stores/types';

/**
 * Hook to access sidebar state and actions.
 * Replaces the old useSidebar hook from SidebarProvider.
 * 
 * @example
 * const { state, toggleSidebar, isMobile } = useSidebar();
 * 
 * if (state === 'collapsed') {
 *   // sidebar is collapsed
 * }
 */
export function useSidebarStore() {
  return useStoreShallow((s) => ({
    state: s.sidebarState,
    toggleSidebar: s.toggleSidebar,
    isMobile: s.isMobile,
  }));
}

/**
 * Component to handle mobile detection and sync with store.
 * Place this at the root of your app.
 * 
 * @example
 * function App() {
 *   return (
 *     <>
 *       <SidebarResponsiveHandler />
 *       <Router>...</Router>
 *     </>
 *   );
 * }
 */
export function SidebarResponsiveHandler(): null {
  const setIsMobile = useBoundStore.use.setIsMobile();
  const setSidebarState = useBoundStore.use.setSidebarState();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const isMobile = e.matches;
      setIsMobile(isMobile);
      // On mobile, default to collapsed
      if (isMobile) {
        setSidebarState('collapsed');
      }
    };

    // Set initial state
    handler(mq);
    
    // Listen for changes
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    
    return () => {
      mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
    };
  }, [setIsMobile, setSidebarState]);

  return null;
}

/**
 * Hook to get only the sidebar state (for components that just need to read).
 * More efficient than useSidebarStore if you don't need actions.
 */
export function useSidebarState(): SidebarState {
  return useBoundStore.use.sidebarState();
}

/**
 * Hook to get only the toggle action (for trigger buttons).
 * More efficient than useSidebarStore if you don't need state.
 */
export function useSidebarToggle() {
  return useBoundStore.use.toggleSidebar();
}

/**
 * Hook to check if on mobile.
 */
export function useIsMobile(): boolean {
  return useBoundStore.use.isMobile();
}
